import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  Paper,
  Alert,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StatsAtGlance } from 'components/emailHistory/StatsAtGlance';
import { AIInsight } from 'components/emailHistory/AIInsight';
import { QuickFilters } from 'components/emailHistory/QuickFilters';
import { StatusChip } from 'components/emailHistory/StatusChip';
import { ExpandedRowIndividual } from 'components/emailHistory/ExpandedRowIndividual';
import { EmailCard } from 'components/emailHistory/EmailCard';
import { QuickStatsBarChart } from 'components/emailHistory/charts';
import { useEmailHistoryFilters } from 'hooks/emailHistory/useEmailHistoryFilters';
import { useIndividualEmails } from 'hooks/emailHistory/useIndividualEmails';
import { useIsMobile } from 'hooks/useIsMobile';
import { ROUTE_CONTACTS, ROUTE_EMAIL_SENDER } from 'constants/routerPath';
import { isoToDateInput, dateRangeToISO, formatDate } from 'utils/emailHistory/dateUtils';
import { DEBOUNCE_DELAY, INDIVIDUAL_EMAIL_SORT_FIELDS } from 'constants/emailHistory';


/**
 * Individual email row component with inline expansion
 */
function IndividualEmailRow({ email, expandedRowIds, setExpandedRowIds, onNavigateToContact }) {
  const isExpanded = expandedRowIds.includes(email.individualEmailId);

  const toggleExpand = () => {
    if (isExpanded) {
      setExpandedRowIds(expandedRowIds.filter(id => id !== email.individualEmailId));
    } else {
      setExpandedRowIds([...expandedRowIds, email.individualEmailId]);
    }
  };

  return (
    <React.Fragment>
      {/* Main Data Row */}
      <TableRow hover sx={{ cursor: 'pointer' }}>
        <TableCell>
          <IconButton
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
            size="small"
            onClick={toggleExpand}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Box>
            <Typography variant="body2">{email.contactName || 'Unknown'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {email.emailAddress}
            </Typography>
          </Box>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Typography variant="body2">{email.subject}</Typography>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Typography variant="body2">{formatDate(email.sendDate)}</Typography>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatusChip status={email.status} />
        </TableCell>
        <TableCell onClick={toggleExpand} align="center">
          {email.status === 'Delivered' || email.status === 'Opened' || email.status === 'Clicked' ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <HorizontalRuleIcon sx={{ color: 'grey.400' }} fontSize="small" />
          )}
        </TableCell>
        <TableCell onClick={toggleExpand} align="center">
          {email.status === 'Opened' || email.status === 'Clicked' ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <HorizontalRuleIcon sx={{ color: 'grey.400' }} fontSize="small" />
          )}
        </TableCell>
        <TableCell onClick={toggleExpand} align="center">
          {email.status === 'Clicked' ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <HorizontalRuleIcon sx={{ color: 'grey.400' }} fontSize="small" />
          )}
        </TableCell>
      </TableRow>

      {/* Expanded Details Row */}
      <TableRow>
        <TableCell sx={{ p: 0, borderBottom: isExpanded ? 1 : 0, bgcolor: 'white' }} colSpan={8}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 3, bgcolor: 'white' }}>
              <ExpandedRowIndividual
                individualEmailId={email.individualEmailId}
                onNavigateToContact={onNavigateToContact}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

/**
 * @component IndividualEmailsTab
 * @description Individual emails tab showing all individual email sends
 * @returns {JSX.Element}
 */
function IndividualEmailsTab() {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { filters, setFilter } = useEmailHistoryFilters();
  const { individualEmails, totalRows, stats, isLoading, error } = useIndividualEmails(filters);
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const isMobile = useIsMobile();

  // Initialize date range from URL params if present, otherwise null (show all emails)
  const [dateRange, setDateRange] = useState(
    filters.dateFrom || filters.dateTo
      ? {
          from: isoToDateInput(filters.dateFrom),
          to: isoToDateInput(filters.dateTo),
        }
      : null
  );

  // Sync date range with URL params when they change
  useEffect(() => {
    if (filters.dateFrom || filters.dateTo) {
      setDateRange({
        from: isoToDateInput(filters.dateFrom),
        to: isoToDateInput(filters.dateTo),
      });
    } else {
      setDateRange(null);
    }
  }, [filters.dateFrom, filters.dateTo]);

  // Sync searchInput with URL params when they change externally
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Debounced search effect - only update URL if search actually changed
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmedSearch = searchInput ? searchInput.trim() : '';
      const currentSearch = filters.search || '';
      
      // Only update if search actually changed
      if (trimmedSearch !== currentSearch) {
        setSearchParams((prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          if (trimmedSearch) {
            newParams.set('search', trimmedSearch);
          } else {
            newParams.delete('search');
          }
          newParams.set('page', '0');
          return newParams;
        });
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchInput, filters.search, setSearchParams]);

  // Handle date range change
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    const { dateFrom, dateTo } = dateRangeToISO(newRange);
    
    // Update both date filters and page in a single setSearchParams call to avoid race conditions
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      if (dateFrom) {
        newParams.set('dateFrom', dateFrom);
      } else {
        newParams.delete('dateFrom');
      }
      if (dateTo) {
        newParams.set('dateTo', dateTo);
      } else {
        newParams.delete('dateTo');
      }
      newParams.set('page', '0');
      return newParams;
    });
  };

  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setFilter('page', newPage);
    setExpandedRowIds([]); // Close expanded rows when changing pages
  };

  const handleChangeRowsPerPage = (event) => {
    setFilter('pageSize', parseInt(event.target.value, 10));
    setFilter('page', 0);
    setExpandedRowIds([]);
  };

  // Handle sorting
  const handleSort = (field) => {
    const isAsc = filters.sortBy === field && filters.sortOrder === 'asc';
    setFilter('sortBy', field);
    setFilter('sortOrder', isAsc ? 'desc' : 'asc');
  };

  // Navigate to contact page
  const handleNavigateToContact = (contactId) => {
    navigate(`${ROUTE_CONTACTS}?contactId=${contactId}`);
  };

  // Navigate to email sender to send new email
  const handleSendNewEmail = () => {
    navigate(ROUTE_EMAIL_SENDER);
  };

  // Handle clearing bulk email filter
  const handleClearBulkEmailFilter = () => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete('bulkEmailId');
      newParams.set('page', '0');
      return newParams;
    });
  };

  // Initialize sortBy to 'sendDate' if not set or invalid
  useEffect(() => {
    if (!filters.sortBy || !INDIVIDUAL_EMAIL_SORT_FIELDS.includes(filters.sortBy)) {
      setFilter('sortBy', 'sendDate');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Define sortable columns
  const columns = [
    { field: 'expand', label: '', sortable: false, width: '50px' },
    { field: 'emailAddress', label: 'Recipient', sortable: true, width: '200px' },
    { field: 'subject', label: 'Subject', sortable: true, width: '220px' },
    { field: 'sendDate', label: 'Send Date', sortable: true, width: '140px' },
    { field: 'status', label: 'Status', sortable: true, width: '110px' },
    { field: 'delivered', label: 'Delivered', sortable: false, width: '80px', align: 'center' },
    { field: 'opened', label: 'Opened', sortable: false, width: '80px', align: 'center' },
    { field: 'linkClicked', label: 'Link Clicked', sortable: false, width: '100px', align: 'center' },
  ];

  // Prepare stats data
  const statsData = [
    { label: 'Individual Emails Sent', value: stats.totalEmails },
    { label: 'Delivered', value: stats.delivered },
    { label: 'Emails Opened', value: stats.opens },
    { label: 'Email Links Clicked', value: stats.linkClicks },
  ];

  // Generate dynamic title based on filters
  const generateStatsTitle = () => {
    let title = "Viewing All Individual Emails";
    const parts = [];

    // Add bulk email filter if applied
    if (filters.bulkEmailId) {
      parts.push(`for Bulk Email #${filters.bulkEmailId}`);
    }

    // Add date filters
    if (filters.dateFrom && filters.dateTo) {
      parts.push(`Between ${formatDate(filters.dateFrom)} and ${formatDate(filters.dateTo)}`);
    } else if (filters.dateFrom) {
      parts.push(`from ${formatDate(filters.dateFrom)}`);
    } else if (filters.dateTo) {
      parts.push(`Up To ${formatDate(filters.dateTo)}`);
    }

    if (parts.length > 0) {
      title += ` ${parts.join(' ')}`;
    }

    return title;
  };

  const statsTitle = generateStatsTitle();
  const statsSubtitle = "An Individual Email is a single email sent to one recipient.";


  return (
    <Box>
      {/* Stats + AI Overview Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <StatsAtGlance stats={statsData} loading={isLoading} title={statsTitle} subtitle={statsSubtitle}>
            <QuickStatsBarChart
              stats={stats}
              loading={isLoading}
              height={200}
            />
          </StatsAtGlance>
        </Grid>
        <Grid item xs={12} md={4}>
          <AIInsight tab="individual" filters={filters} />
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Quick Filters and Data Table */}
      <Paper>
        <QuickFilters
          searchValue={searchInput}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search email or subject..."
          showDateRange={true}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          dateRangeDefault="30days"
          startButton={
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleSendNewEmail}
              fullWidth={isMobile}
            >
              Send new email
            </Button>
          }
          actionButton={
            filters.bulkEmailId ? (
              <Button
                variant="outlined"
                color="inherit"
                size="medium"
                startIcon={<ClearIcon />}
                onClick={handleClearBulkEmailFilter}
                sx={{ borderColor: 'grey.300' }}
                fullWidth={isMobile}
              >
                Clear Bulk Email Filter
              </Button>
            ) : null
          }
        />
        {isMobile ? (
          <Box sx={{ p: 2 }}>
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : individualEmails.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No emails found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No individual emails match your current filters. Try adjusting your search or date range.
                </Typography>
              </Box>
            ) : (
              <>
                {individualEmails.map((email) => {
                  const isExpanded = expandedRowIds.includes(email.individualEmailId);
                  return (
                    <EmailCard
                      key={email.individualEmailId}
                      data={{
                        contactName: email.contactName || 'Unknown',
                        emailAddress: email.emailAddress,
                        subject: email.subject,
                        sendDate: formatDate(email.sendDate),
                        status: email.status,
                      }}
                      type="individual"
                      isExpanded={isExpanded}
                      onToggleExpand={() => {
                        if (isExpanded) {
                          setExpandedRowIds(expandedRowIds.filter(id => id !== email.individualEmailId));
                        } else {
                          setExpandedRowIds([...expandedRowIds, email.individualEmailId]);
                        }
                      }}
                      expandedContent={
                        isExpanded ? (
                          <ExpandedRowIndividual
                            individualEmailId={email.individualEmailId}
                            onNavigateToContact={handleNavigateToContact}
                          />
                        ) : null
                      }
                    />
                  );
                })}
                <TablePagination
                  component="div"
                  count={totalRows}
                  page={filters.page}
                  onPageChange={handleChangePage}
                  rowsPerPage={filters.pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[]}
                />
              </>
            )}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        sx={{ width: column.width, fontWeight: 600 }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={filters.sortBy === column.field}
                            direction={filters.sortBy === column.field ? filters.sortOrder : 'asc'}
                            onClick={() => handleSort(column.field)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : individualEmails.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No emails found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          No individual emails match your current filters. Try adjusting your search or date range.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    individualEmails.map((email) => (
                      <IndividualEmailRow
                        key={email.individualEmailId}
                        email={email}
                        expandedRowIds={expandedRowIds}
                        setExpandedRowIds={setExpandedRowIds}
                        onNavigateToContact={handleNavigateToContact}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRows}
              page={filters.page}
              onPageChange={handleChangePage}
              rowsPerPage={filters.pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

export default IndividualEmailsTab;
